import React, { useEffect, useMemo } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { m } from 'framer-motion'

// Components
import RDPHeader from '../Components/Header/RDPHeader'
import RDPFooter from '../Components/Footers/RDPFooter'

// Hooks
import { useCategoriesWithProducts } from '../hooks/useCategoriesWithProducts'

// Animation variants
const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
}

// Données statiques des catégories (déplacées hors du composant pour éviter les recréations)
const ALL_CATEGORIES_DATA = [
    {
      title: "Tout",
      img: require("../Assets/img/products/Tout.JPG"),
      link: "/produits/tout",
      description: "Parcourez l'intégralité de notre catalogue"
    },
    {
      title: "Vin",
      img: require("../Assets/img/products/Vins.JPG"),
      link: "/produits/vin",
      description: "Découvrez notre gamme complète de bouteilles pour vins"
    },
    {
      title: "Bière",
      img: require("../Assets/img/products/Bière.JPG"),
      link: "/produits/biere",
      description: "Bouteilles et flacons adaptés à tous types de bières"
    },
    {
      title: "Spiritueux",
      img: require("../Assets/img/products/Spiritueux.JPG"),
      link: "/produits/spiritueux",
      description: "Flacons élégants pour vos spiritueux premium"
    },
    {
      title: "Bocaux",
      img: require("../Assets/img/products/Bocaux.JPG"),
      link: "/produits/bocaux",
      description: "Bocaux en verre pour conserves et confitures"
    },
    {
      title: "Soft drinks",
      img: require("../Assets/img/products/Soft Drinks.JPG"),
      link: "/produits/soft-drinks",
      description: "Bouteilles pour sodas, jus et boissons sans alcool"
    },
    {
      title: "Huiles et Sauces",
      img: require("../Assets/img/products/Huile.JPG"),
      link: "/produits/huiles-sauces",
      description: "Bouteilles pour huiles d'olive, vinaigres et sauces"
    },
    {
      title: "Bouteilles en PET",
      img: require("../Assets/img/markets/pet.jpg"),
      link: "/produits/pet",
      description: "Solutions légères en plastique PET"
    },
    {
      title: "Bouteilles décorées",
      img: require("../Assets/img/markets/decorees-optimized.jpg"),
      link: "/produits/decorees",
      description: "Bouteilles personnalisées et décorées sur mesure"
    }
]

const ProductsCatalogPage = (props) => {
  const { categories: categoriesWithProducts } = useCategoriesWithProducts()

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filtrer les catégories pour ne montrer que celles ayant des produits
  const categories = useMemo(() => {
    return ALL_CATEGORIES_DATA.filter(category => {
      // Toujours garder "Tout"
      if (category.link === '/produits/tout') return true
      // Extraire le slug de la catégorie depuis le lien
      const slug = category.link.replace('/produits/', '')
      return categoriesWithProducts.includes(slug)
    })
  }, [categoriesWithProducts])

  return (
    <div style={props.style}>
      {/* Header */}
      <RDPHeader {...props} />

      {/* Hero Section */}
      <section className="pt-[130px] pb-[80px] lg:pt-[120px] lg:pb-[60px] md:pt-[110px] md:pb-[50px] sm:pt-[100px] sm:pb-[40px] bg-white">
        <Container>
          <Row className="justify-center mb-20">
            <Col lg={8} md={10} className="text-center">
              <m.h1
                className="heading-2 font-serif font-semibold text-glass-darkest mb-[25px]"
                {...fadeIn}
              >
                Nos gammes de produits
              </m.h1>
              <m.p
                className="text-glass-dark text-xlg font-serif"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                Sélectionnez une catégorie pour découvrir notre catalogue complet
              </m.p>
            </Col>
          </Row>

          {/* Grille des catégories - 3 colonnes */}
          <Row className="justify-center">
            {categories.map((category, index) => (
              <Col lg={4} md={6} sm={12} className="mb-[30px]" key={index}>
                <m.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={category.link} className="block group">
                    <div className="relative overflow-hidden rounded-lg h-[450px] md:h-[400px] sm:h-[380px]">
                      {/* Image */}
                      <img
                        src={category.img}
                        alt={category.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />

                      {/* Overlay gradient */}
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-glass-darkest opacity-80 group-hover:opacity-90 transition-opacity"></div>

                      {/* Contenu */}
                      <div className="absolute bottom-0 left-0 w-full p-[40px] text-white">
                        <h3 className="font-serif font-semibold text-[28px] mb-[15px]">
                          {category.title}
                        </h3>
                        <p className="text-white/80 text-md mb-[20px]">
                          {category.description}
                        </p>
                        <span className="text-white font-serif uppercase text-sm group-hover:text-glass-accentLight transition-colors inline-flex items-center gap-2">
                          Voir les produits
                          <i className="fas fa-arrow-right"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                </m.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <RDPFooter />
    </div>
  )
}

export default ProductsCatalogPage
